import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useCredits } from '@/hooks/useCredits';
import { Logo } from './Logo';

// Below this width the sidebar always renders icons-only to free horizontal
// space for the workspace. Matches Tailwind's `xl:` breakpoint (1280px).
const COLLAPSE_BELOW_PX = 1280;

export interface StepDef {
  num: number;
  label: string;
  icon: string;
}

interface AppLayoutProps {
  currentStep: number;
  /** Per-step summary strings (one per step, matching `steps.length`). When
   *  the entry for a step is non-null, the sidebar shows that string in
   *  place of the generic step label ("Platform" → "Instagram Post", etc.).
   *  Optional for backward compat. */
  stepSummaries?: (string | null)[];
  /** Step definitions for this wizard route. Optional — defaults to the
   *  8-step DEFAULT_STEPS below. Pages can pass a different array (e.g.
   *  the 6-step social-post route) to render a different sidebar nav. */
  steps?: StepDef[];
  onStepClick: (step: number) => void;
  onExitGallery: () => void;
  onNewPost: () => void;
  children: React.ReactNode;
}

// Default 8-step layout used by the Product Shot + Carousel routes (and by
// any caller that doesn't pass its own `steps` prop). The Social Post route
// passes a 6-step variant — see SOCIAL_POST_STEPS in app/page.tsx.
const DEFAULT_STEPS: StepDef[] = [
  { num: 1, label: 'Type',       icon: 'ri-stack-line' },
  { num: 2, label: 'Platform',   icon: 'ri-smartphone-line' },
  { num: 3, label: 'Product',    icon: 'ri-upload-cloud-line' },
  { num: 4, label: 'Details',    icon: 'ri-sparkling-2-line' },
  { num: 5, label: 'Background', icon: 'ri-palette-line' },
  { num: 6, label: 'Quality',    icon: 'ri-vip-crown-line' },
  { num: 7, label: 'Preview',    icon: 'ri-image-line' },
  { num: 8, label: 'Add Text',   icon: 'ri-text' },
];

export default function AppLayout({ currentStep, stepSummaries, steps = DEFAULT_STEPS, onStepClick, onExitGallery, onNewPost, children }: AppLayoutProps) {
  // Credit balance pill in the header. null while loading; we show "—" placeholder.
  const { balance, planTier, trialEndsAt, loading: creditsLoading } = useCredits();
  const isTrialing = !!trialEndsAt && new Date(trialEndsAt).getTime() > Date.now();
  const planName = planTier.charAt(0).toUpperCase() + planTier.slice(1);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  // Initialize synchronously from the current viewport so the sidebar doesn't
  // render expanded for a frame on small screens before the effect fires.
  const [forceCollapse, setForceCollapse] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia(`(max-width: ${COLLAPSE_BELOW_PX - 1}px)`).matches,
  );
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia(`(max-width: ${COLLAPSE_BELOW_PX - 1}px)`);
    const update = () => setForceCollapse(mq.matches);
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  // User toggle works on xl+; on smaller screens we always show icons-only.
  const collapsed = forceCollapse || sidebarCollapsed;

  return (
    // Viewport-locked shell — strict no-scroll page. The wizard panel inside
    // has its own internal overflow-y-auto for long step content, so we never
    // need the body to scroll. Don't switch this to min-h-* — that lets the
    // panel grow with content (e.g. the preview image) and pushes the
    // bottom-nav Continue button below the fold.
    <div className="h-screen w-full flex overflow-hidden bg-surface">
      {/* LEFT SIDEBAR */}
      <aside
        className={`flex-shrink-0 bg-surface-sidebar border-r border-surface-dark/40 flex flex-col transition-all duration-300 ${
          collapsed ? 'w-16' : 'w-64'
        }`}
      >
        {/* Logo area */}
        <button
          onClick={onExitGallery}
          className="h-16 flex items-center gap-3 px-4 border-b border-surface-dark/30 cursor-pointer text-left hover:bg-surface/50 transition-colors"
        >
          {collapsed ? (
            <Logo variant="mark" height={56} className="flex-shrink-0" />
          ) : (
            <Logo height={50} className="flex-shrink-0" />
          )}
        </button>

        {/* NAVIGATION */}
        <nav className="flex-1 py-4 px-3 space-y-0.5">
          {/* New Creative — primary action above the Post Creator step list.
              Confirmation modal lives in the parent (page.tsx) and only
              triggers if there's unsaved progress. */}
          <button
            onClick={onNewPost}
            title={collapsed ? 'New Creative' : undefined}
            className={`w-full flex items-center gap-2.5 px-4 py-3 mb-6 rounded-full text-xs font-medium transition-colors cursor-pointer bg-primary hover:bg-primary-hover text-white shadow-lg shadow-primary/15 ${
              collapsed ? 'justify-center' : ''
            }`}
          >
            {/* Icon tile mirrors the step-button structure (24x24 rounded
                square with bg + icon). Keeps "New Creative" visually consistent
                with the nav rows below — same shape, same gap, same font
                weight — while still reading as the primary CTA via the
                solid pink bg. */}
            <div className="w-6 h-6 rounded-lg bg-white/20 flex items-center justify-center flex-shrink-0">
              <i className="ri-add-line text-xs text-white" />
            </div>
            {!collapsed && <span>New Creative</span>}
          </button>

          <div className={`flex items-center gap-2 px-3 py-1.5 mb-1 ${collapsed ? 'justify-center' : ''}`}>
            <div className="w-6 h-6 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
              <i className="ri-magic-line text-accent text-xs" />
            </div>
            {!collapsed && (
              <div className="flex-1">
                <span className="text-xs font-semibold text-dark">Post Creator</span>
                <p className="text-xs text-dark-muted leading-tight">{steps.length} steps to publish</p>
              </div>
            )}
          </div>

          {steps.map((step) => {
            const isActive = currentStep === step.num;
            const isCompleted = currentStep > step.num;
            const isDisabled = step.num > currentStep + 1;

            return (
              <button
                key={step.num}
                onClick={() => !isDisabled && onStepClick(step.num)}
                disabled={isDisabled}
                className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-xs font-medium transition-all group ${
                  isActive
                    ? 'bg-primary-light text-primary'
                    : isCompleted
                    ? 'text-dark hover:bg-surface'
                    : 'text-dark-muted hover:bg-surface/60'
                } ${isDisabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}
                title={collapsed ? step.label : undefined}
              >
                <div
                  className={`w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0 text-xs transition-colors ${
                    isActive
                      ? 'bg-white text-primary'
                      : isCompleted
                      ? 'bg-primary/10 text-primary'
                      : 'bg-surface text-dark-muted'
                  }`}
                >
                  {isCompleted ? (
                    <i className="ri-check-line text-xs" />
                  ) : (
                    <i className={`${step.icon} text-xs`} />
                  )}
                </div>
                {!collapsed && (
                  <div className="flex-1 text-left leading-tight">
                    <div className="flex items-center justify-between">
                      <span>Step {step.num}</span>
                      {/* Always render the check icon and toggle visibility,
                          rather than conditionally mounting it. Mounting on
                          completion causes the flex row to reflow when the
                          user advances steps — visible as a jitter in the
                          sidebar. visibility:hidden keeps the layout box
                          reserved at all times, so step transitions are
                          purely color/state animations with zero layout. */}
                      <i
                        className={`ri-check-double-line text-xs text-primary ${
                          isCompleted ? '' : 'invisible'
                        }`}
                      />
                    </div>
                    {/* Show the user's actual selection (e.g. "Instagram
                        Post") when it's available — surfaces context from
                        completed/active steps in the stepper itself. Falls
                        back to the generic step label ("Platform") before
                        the user makes a choice.
                        line-clamp-2 wraps long values onto a 2nd line and
                        caps there with ellipsis. break-words lets long
                        single tokens (filenames) wrap mid-string.
                        min-h-[2.5em] reserves the vertical space for 2
                        lines whether the value is 1 or 2 lines, so step
                        rows stay the same height across the wizard. */}
                    <p
                      className={`text-xs line-clamp-2 break-words min-h-[2.5em] ${
                        isActive ? 'text-primary/70' : 'text-dark-muted'
                      }`}
                      title={stepSummaries?.[step.num - 1] || step.label}
                    >
                      {stepSummaries?.[step.num - 1] || step.label}
                    </p>
                  </div>
                )}
              </button>
            );
          })}
        </nav>

        {/* Toggle + Bottom. The toggle is only useful at xl+ — below that
            we force icons-only, so hide the control. */}
        <div className="p-2 border-t border-surface-dark/30">
          {!forceCollapse && (
            <button
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="w-full flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-xl text-xs text-dark-muted hover:bg-surface transition-colors cursor-pointer"
            >
              <i className={`${collapsed ? 'ri-menu-unfold-line' : 'ri-menu-fold-line'} text-xs`} />
              {!collapsed && <span className="text-xs">Collapse</span>}
            </button>
          )}

          {!collapsed && (
            <div className="mt-2 p-2.5 rounded-xl bg-surface border border-surface-dark/30">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <i className="ri-user-3-line text-primary text-xs" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-dark truncate">
                    {user?.displayName || user?.email?.split('@')[0] || 'Creator'}
                  </p>
                  <p className="text-xs text-dark-muted truncate">
                    {!user
                      ? 'Not signed in'
                      : planTier === 'free'
                      ? 'Free'
                      : isTrialing
                      ? `${planName} · Trial`
                      : `${planName} plan`}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </aside>

      {/* MAIN CONTENT AREA */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* TOP BAR */}
        <header className="h-16 flex-shrink-0 bg-white border-b border-surface-dark/40 flex items-center justify-between px-5">
          <div className="flex items-center gap-3">
            <h1 className="font-display font-bold text-dark text-base">
              {steps[currentStep - 1]?.label}
            </h1>
            <span className="px-2 py-0.5 rounded-full bg-primary-light text-primary text-xs font-semibold">
              Step {currentStep} of {steps.length}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {/* Credit balance pill — always visible so running out feels like
                a real constraint, not a surprise. Plan tier shown alongside
                so the user knows where their balance comes from. */}
            <div
              className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-accent-light text-accent text-xs font-bold"
              title={planTier === 'free' ? 'Free' : isTrialing ? `${planName} · Trial` : `${planName} plan`}
            >
              <i className="ri-copper-coin-line text-sm" />
              <span>
                {creditsLoading || balance === null ? '—' : balance}
                <span className="ml-1 text-accent/70 font-normal">credits</span>
              </span>
            </div>
            <div className="w-px h-5 bg-surface-dark hidden sm:block" />
            <button
              onClick={onExitGallery}
              className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-bold text-dark-muted hover:bg-surface hover:text-dark transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line text-xs" />
              Exit to My Creatives
            </button>
            <div className="w-px h-5 bg-surface-dark hidden sm:block" />
            <a
              href="/"
              className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-bold text-dark-muted hover:bg-surface hover:text-dark transition-colors cursor-pointer"
            >
              <i className="ri-home-4-line text-xs" />
              Home
            </a>
            <div className="w-px h-5 bg-surface-dark hidden sm:block" />
            {user && (
              <a
                href="/auth/profile"
                className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-bold text-dark-muted hover:bg-surface hover:text-dark transition-colors cursor-pointer"
              >
                <i className="ri-user-settings-line text-xs" />
                Account
              </a>
            )}
          </div>
        </header>

        {/* CONTENT — fills remaining height, no scroll; centered at 1440px.
            overflow-hidden is required to keep the wizard panel viewport-
            bound; the panel's own overflow-y-auto handles internal scroll. */}
        <main className="flex-1 overflow-hidden p-5">
          <div className="h-full max-w-[1440px] mx-auto">{children}</div>
        </main>
      </div>
    </div>
  );
}